import {
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
} from "@material-ui/icons";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import React, { useCallback, useEffect, useState } from "react";
import FormError from "../Components/FormError";
import SearchCaseForm from "../Components/SearchCase";
import { getDisputeEmailHistory } from "../service";
import { displayEmail, safeDateFormat, getCaseReferenceNumber } from "../utils";

// SendGrid event names, as returned in `latest_event`. Anything unknown falls
// back to the neutral chip.
const EVENT_COLORS = {
  delivered: { bg: "#E4F5EA", color: TEXT.success },
  open: { bg: "#E6F0FD", color: TEXT.info },
  click: { bg: "#EDE7F6", color: TEXT.brand },
  processed: { bg: "#F2F4F7", color: TEXT.secondary },
  deferred: { bg: "#FDF2CF", color: TEXT.warning },
  bounce: { bg: "#FDE7E7", color: TEXT.error },
  dropped: { bg: "#FDE7E7", color: TEXT.error },
  blocked: { bg: "#FDE7E7", color: TEXT.error },
  spamreport: { bg: "#FDE7E7", color: TEXT.error },
  unsubscribe: { bg: "#F2F4F7", color: TEXT.secondary },
};
const NEUTRAL_EVENT = { bg: "#F2F4F7", color: TEXT.secondary };

const PARTY_TYPE_LABELS = {
  claimant: "Claimant",
  respondent: "Respondent",
  claimant_counsel: "Claimant Counsel",
  respondent_counsel: "Respondent Counsel",
  professional: "Professional",
  sub_admin_tiac_rv: "Admin",
};

const DATE_TIME_FORMAT = "dd/MM/yyyy, hh:mm a";

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const partyLabel = (recipient) => {
  if (recipient?.profile_type === "arbitrator_rv") return "Arbitrator";
  return (
    PARTY_TYPE_LABELS[recipient?.party_type] ||
    PARTY_TYPE_LABELS[recipient?.profile_type] ||
    titleCase(recipient?.party_type || recipient?.profile_type) ||
    "—"
  );
};

const recipientName = (recipient) => (recipient?.first_name || "").trim() || "—";

const accountCreationNote = (recipient, caseNumber) => {
  const info = recipient?.account_creation_email;
  if (!info?.sent) return "";

  const sentForCase = info.sent_for_case;
  if (
    !sentForCase ||
    !caseNumber ||
    String(sentForCase) === String(caseNumber)
  ) {
    return "";
  }

  const when = info.sent_at
    ? ` on ${safeDateFormat(info.sent_at, DATE_TIME_FORMAT)}`
    : "";
  return `Account creation email was already sent to this recipient${when} for case ${sentForCase}.`;
};

// Newest first, so the most recent email a party received is the one on top.
const sortedMails = (mails) =>
  [...(mails || [])].sort(
    (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
  );

const useStyles = makeStyles((theme) => ({
  container: {
    textAlign: "left",
  },
  helperText: {
    fontSize: "0.875rem",
    color: TEXT.muted,
    marginBottom: theme.spacing(2),
  },
  detailsPaper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    borderRadius: 8,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  detailBox: {
    backgroundColor: "#F9FAFB",
    padding: theme.spacing(2),
    borderRadius: 6,
    border: "1px solid #EAECF0",
    marginBottom: theme.spacing(3),
  },
  detailLabel: {
    fontSize: "0.75rem",
    color: TEXT.muted,
    fontWeight: 500,
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    fontSize: "0.95rem",
    color: TEXT.strong,
    fontWeight: 600,
    wordBreak: "break-word",
  },
  statTile: {
    padding: theme.spacing(2),
    borderRadius: 6,
    border: "1px solid #EAECF0",
    backgroundColor: "#FFFFFF",
    // height: "100%",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: TEXT.strong,
  },
  tableHeadRow: {
    backgroundColor: "#f5f5f5",
    "& th": {
      padding: "10px",
      fontWeight: 600,
      fontSize: "0.875rem",
    },
  },
  tableBodyCell: {
    padding: "10px",
    fontSize: "0.875rem",
  },
  clickableRow: {
    cursor: "pointer",
  },
  subText: {
    fontSize: "0.75rem",
    color: TEXT.muted,
  },
  // Marks a row whose address is one of the party's additional emails.
  additionalTag: {
    marginLeft: theme.spacing(1),
    height: 20,
    fontSize: "0.7rem",
    fontWeight: 600,
    backgroundColor: "#EAF1FB",
    color: "#1F5FA8",
  },
  accountNote: {
    backgroundColor: "#E4F5EA",
    color: TEXT.strong,
    border: "1px solid #B7E4C7",
    borderRadius: 6,
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
  },
}));

function EventChip({ mail }) {
  const event = mail?.latest_event;

  // No webhook event has come back yet, so all we know is whether SendGrid
  // reported the message as delivered.
  if (!event) {
    const s = mail?.is_delivered ? EVENT_COLORS.delivered : NEUTRAL_EVENT;
    return (
      <Chip
        label={mail?.is_delivered ? "Delivered" : "Sent"}
        size="small"
        style={{ backgroundColor: s.bg, color: s.color, fontWeight: 600 }}
      />
    );
  }

  const s = EVENT_COLORS[event] || NEUTRAL_EVENT;
  return (
    <Chip
      label={titleCase(event)}
      size="small"
      style={{ backgroundColor: s.bg, color: s.color, fontWeight: 600 }}
    />
  );
}

function StatTile({ label, value, classes, color }) {
  return (
    <Paper className={classes.statTile} elevation={0}>
      <Typography className={classes.detailLabel}>{label}</Typography>
      <Typography className={classes.statValue} style={color ? { color } : {}}>
        {value}
      </Typography>
    </Paper>
  );
}

function RecipientRow({ recipient, serialNo, classes, defaultOpen, caseNumber }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const mails = sortedMails(recipient.mails);
  const deliveredCount = mails.filter((m) => m.is_delivered).length;
  const accountNote = accountCreationNote(recipient, caseNumber);

  return (
    <>
      <TableRow
        hover
        className={classes.clickableRow}
        onClick={() => setOpen((o) => !o)}
      >
        <TableCell className={classes.tableBodyCell}>{serialNo}</TableCell>
        <TableCell className={classes.tableBodyCell}>
          {recipientName(recipient)}
        </TableCell>
        <TableCell className={classes.tableBodyCell} style={{ overflowWrap: "anywhere" }}>
          {displayEmail(recipient.email)}
          {recipient.additional_email && (
            <Chip label="Additional email" size="small" className={classes.additionalTag} />
          )}
        </TableCell>
        <TableCell className={classes.tableBodyCell}>
          {partyLabel(recipient)}
        </TableCell>
        <TableCell className={classes.tableBodyCell}>{mails.length}</TableCell>
        <TableCell className={classes.tableBodyCell}>
          {deliveredCount}
          {mails.length > deliveredCount && (
            <span className={classes.subText}>
              {" "}
              ({mails.length - deliveredCount} not delivered)
            </span>
          )}
        </TableCell>
        <TableCell className={classes.tableBodyCell} align="center">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={7}
          style={{
            paddingTop: 0,
            paddingBottom: 0,
            borderBottom: "1px solid #eee",
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={2}>
              <Box p={1.5}>
                <Typography style={{ fontWeight: 600 }}>
                  Emails sent to {recipientName(recipient)}
                  {recipient.additional_email &&
                    ` (additional email: ${displayEmail(recipient.email)})`}
                </Typography>
              </Box>
              {accountNote && (
                <Box className={classes.accountNote}>
                  <Typography style={{fontSize: '0.875rem'}}>{accountNote}</Typography>
                </Box>
              )}
              {mails.length === 0 ? (
                <Box p={1.5}>
                  <Typography color="textSecondary" variant="body2">
                    No emails have been sent to this recipient yet.
                  </Typography>
                </Box>
              ) : (
                <Paper
                  style={{
                    boxShadow: "none",
                    border: "1px solid #ddd",
                    borderBottom: "none",
                  }}
                >
                  <Table size="small" style={{ maxWidth: "100%", margin: 0 }}>
                    <TableHead>
                      <TableRow className={classes.tableHeadRow}>
                        <TableCell>Subject</TableCell>
                        <TableCell>Sent At</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last Event At</TableCell>
                        <TableCell>Delivered At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mails.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell className={classes.tableBodyCell}>
                            {m.subject
                              ? m.subject.replace(/\{\{\s*case_number\s*\}\}/g, caseNumber || "")
                              : "—"}
                          </TableCell>
                          <TableCell className={classes.tableBodyCell}>
                            {safeDateFormat(m.timestamp, DATE_TIME_FORMAT)}
                          </TableCell>
                          <TableCell className={classes.tableBodyCell}>
                            <EventChip mail={m} />
                          </TableCell>
                          <TableCell className={classes.tableBodyCell}>
                            {safeDateFormat(
                              m.latest_event_time,
                              DATE_TIME_FORMAT
                            )}
                          </TableCell>
                          <TableCell className={classes.tableBodyCell}>
                            {m.is_delivered
                              ? safeDateFormat(
                                  m.delivered_time,
                                  DATE_TIME_FORMAT
                                )
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/**
 * Emails Dashboard tab.
 *
 * Search a case, then see every email the platform has sent for it, grouped by
 * recipient, with the latest SendGrid delivery event per email
 * (GET /bac/dispute-email-history/?case_number=...).
 */
export default function EmailsDashboard() {
  const classes = useStyles();

  const [searchedCase, setSearchedCase] = useState(null);
  const [caseNumber, setCaseNumber] = useState("");
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  const fetchHistory = useCallback(async (number) => {
    if (!number) return;
    setLoading(true);
    setError("");
    try {
      const response = await getDisputeEmailHistory(number);
      const data = response.data || {};
      // The endpoint reports its own failures in the body, so a 200 is not
      // proof of success.
      if (data.success === false) {
        setHistory(null);
        setError(data.message || "Could not load the email history.");
      } else {
        setHistory(data);
      }
    } catch (err) {
      console.error("Email history fetch failed:", err);
      const data = err?.response?.data;
      setHistory(null);
      setError(
        data?.message ||
          data?.error ||
          data?.detail ||
          "Could not load the email history. Please try again."
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (caseNumber) fetchHistory(caseNumber);
  }, [caseNumber, fetchHistory]);

  const handleSearchSuccess = (caseData, formattedCaseNumber) => {
    const number =
      formattedCaseNumber ||
      caseData?.case_number ||
      caseData?.tiac_custom_dispute_id ||
      "";
    setSearchedCase(caseData);
    setHistory(null);
    setFilter("");
    setCaseNumber(number);
    if (!number) {
      setError("This case has no case number, so its emails cannot be listed.");
      return;
    }
    setError("");
    if (number === caseNumber) {
      // Same case searched again: the effect will not re-run, so refetch here.
      fetchHistory(number);
    }
  };

  const handleSearchError = () => {
    setSearchedCase(null);
    setCaseNumber("");
    setHistory(null);
    setError("");
  };

  const recipients = history?.emails || [];

  // A query matching the recipient keeps all of their emails; otherwise the
  // recipient is kept only for the emails whose subject matches.
  const query = filter.trim().toLowerCase();
  const filteredRecipients = !query
    ? recipients
    : recipients
        .map((r) => {
          const matchesRecipient = [
            r.first_name,
            r.email,
            r.party_type,
            r.profile_type,
            r.additional_email ? "additional email" : "",
          ].some((f) => f && String(f).toLowerCase().includes(query));
          if (matchesRecipient) return r;
          const mails = (r.mails || []).filter((m) => {
            const subject = m.subject
              ? m.subject.replace(/\{\{\s*case_number\s*\}\}/g, caseNumber || searchedCase?.tiac_custom_dispute_id || "")
              : "";
            return subject.toLowerCase().includes(query);
          });
          return mails.length ? { ...r, mails } : null;
        })
        .filter(Boolean);

  const allMails = recipients.reduce((acc, r) => acc.concat(r.mails || []), []);
  const deliveredCount = allMails.filter((m) => m.is_delivered).length;
  const notDeliveredCount = allMails.length - deliveredCount;

  const caseTitle =
    searchedCase?.name ||
    searchedCase?.case_title ||
    searchedCase?.title ||
    [searchedCase?.claimant?.name, searchedCase?.respondent?.name]
      .filter(Boolean)
      .join(" vs. ") ||
    "N/A";

  const refNo = getCaseReferenceNumber(searchedCase);

  return (
    <Box className={classes.container}>

      <Typography className={classes.helperText}>
        Search a case to see every email sent for it, grouped by recipient,
        along with its latest delivery status.
      </Typography>

      <Box mb={3}>
        <SearchCaseForm
          handleSearchSuccess={handleSearchSuccess}
          handleSearchError={handleSearchError}
        />
      </Box>

      {searchedCase && (
        <Paper className={classes.detailsPaper} elevation={1}>
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <Typography
                variant="h6"
                className={classes.sectionTitle}
                color="primary"
                gutterBottom
              >
                Email History:{" "}
                {caseNumber || searchedCase.tiac_custom_dispute_id}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => fetchHistory(caseNumber)}
                  disabled={loading || !caseNumber}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          <Box className={classes.detailBox}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={refNo ? 3 : 6}>
                <Typography className={classes.detailLabel}>
                  Case Number
                </Typography>
                <Typography className={classes.detailValue}>
                  {caseNumber || searchedCase.tiac_custom_dispute_id || "N/A"}
                </Typography>
              </Grid>
              {refNo && (
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.detailLabel}>
                    {refNo.label}
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {refNo.value}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography className={classes.detailLabel}>
                  Case Title
                </Typography>
                <Typography className={classes.detailValue}>
                  {caseTitle}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <FormError message={error} style={{ marginBottom: 16 }} />

          {loading ? (
            <Box p={4}>
              <CircularCenteredLoader />
            </Box>
          ) : (
            history && (
              <>
                <Box mb={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <StatTile
                        label="Recipients"
                        value={recipients.length}
                        classes={classes}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatTile
                        label="Emails Sent"
                        value={allMails.length}
                        classes={classes}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatTile
                        label="Delivered"
                        value={deliveredCount}
                        classes={classes}
                        color="#27AE60"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatTile
                        label="Not Delivered"
                        value={notDeliveredCount}
                        classes={classes}
                        color={notDeliveredCount ? "#EB5757" : undefined}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box mb={2} maxWidth={360}>
                  <TextField
                    label="Filter by recipient or subject"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </Box>

                <Paper
                  style={{
                    width: "100%",
                    overflowX: "auto",
                    boxShadow: "none",
                    border: "1px solid #ddd",
                  }}
                >
                  <TableContainer>
                    <Table
                      size="small"
                      style={{ maxWidth: "100%", marginTop: 0 }}
                    >
                      <TableHead>
                        <TableRow className={classes.tableHeadRow}>
                          <TableCell>S.No.</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Emails</TableCell>
                          <TableCell>Delivered</TableCell>
                          <TableCell align="center" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRecipients.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              align="center"
                              style={{ padding: "2rem" }}
                            >
                              <Typography color="textSecondary">
                                {recipients.length === 0
                                  ? "No emails have been sent for this case yet."
                                  : "No recipients or emails match your filter."}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredRecipients.map((r, idx) => (
                            <RecipientRow
                              key={`${r.email}-${idx}`}
                              recipient={r}
                              serialNo={idx + 1}
                              classes={classes}
                              defaultOpen={!!query}
                              caseNumber={caseNumber || searchedCase?.tiac_custom_dispute_id || ""}
                            />
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )
          )}
        </Paper>
      )}
    </Box>
  );
}